import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Eye, EyeOff, Pencil, Trash2, Plus, ShieldCheck, Wallet } from "lucide-react";
import client from "@/api";
import { assetUrl } from "@/context/TenantContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const fmt = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const err = (e, f) => toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : f);
const Card = ({ children, className = "" }) => <div className={`rounded-2xl bg-white p-6 ${className}`}>{children}</div>;

export const LogoUpload = ({ onUploaded }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await client.post("/tenant-admin/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(r.data.logo_url);
      toast.success("Logo uploaded");
    } catch (x) { err(x, "Upload failed"); } finally { setBusy(false); e.target.value = ""; }
  };
  return (
    <>
      <input ref={ref} type="file" accept=".png,.jpg,.jpeg,.webp,.svg" hidden onChange={pick} data-testid="branding-logo-file" />
      <Button type="button" variant="outline" onClick={() => ref.current?.click()} disabled={busy} data-testid="branding-logo-upload-btn">
        <Upload className="mr-1.5 h-4 w-4" />{busy ? "Uploading…" : "Upload"}
      </Button>
    </>
  );
};

const RuleRow = ({ label, rule, onChange, id }) => (
  <div className="grid items-center gap-3 border-b border-slate-100 py-4 sm:grid-cols-[160px_180px_1fr]">
    <b className="text-[#1B1F5B]">{label}</b>
    <div className="flex overflow-hidden rounded-lg border">
      {["flat", "percent"].map((t) => (
        <button key={t} type="button" onClick={() => onChange({ ...rule, type: t })} data-testid={`markup-${id}-${t}`}
          className={`flex-1 px-3 py-2 text-sm ${rule.type === t ? "bg-[#1B1F5B] text-white" : "text-slate-600"}`}>
          {t === "flat" ? "Flat ₹" : "Percent %"}
        </button>
      ))}
    </div>
    <Input type="number" min="0" value={rule.value} onChange={(e) => onChange({ ...rule, value: e.target.value })}
      className="max-w-[160px]" data-testid={`markup-${id}-value`} />
  </div>
);

export const MarkupPanel = () => {
  const [m, setM] = useState(null);
  useEffect(() => { client.get("/tenant-admin/markup").then((r) => setM(r.data)); }, []);
  if (!m) return null;
  const save = async () => {
    const body = Object.fromEntries(Object.entries(m).map(([k, r]) => [k, { type: r.type, value: Number(r.value) || 0 }]));
    try { setM((await client.put("/tenant-admin/markup", body)).data); toast.success("Markup saved — live on your site now"); }
    catch (x) { err(x, "Could not save markup"); }
  };
  return (
    <Card>
      <h3 className="text-lg font-bold text-[#1B1F5B]">Your margin on every sale</h3>
      <p className="mt-1 text-sm text-slate-500">Added on top of the net fare shown to your customers. Flight flat markup is per passenger; hotel and holiday markup is per booking/package price.</p>
      <div className="mt-4">
        <RuleRow id="flight" label="Flights" rule={m.flight} onChange={(r) => setM({ ...m, flight: r })} />
        <RuleRow id="hotel" label="Hotels" rule={m.hotel} onChange={(r) => setM({ ...m, hotel: r })} />
        <RuleRow id="holiday" label="Holidays" rule={m.holiday} onChange={(r) => setM({ ...m, holiday: r })} />
      </div>
      <Button onClick={save} className="mt-5 bg-[#1B1F5B] hover:bg-[#141844]" data-testid="markup-save-btn">Save markup</Button>
    </Card>
  );
};

export const PaymentsPanel = () => {
  const [p, setP] = useState(null);
  const [kid, setKid] = useState("");
  const [sec, setSec] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { client.get("/tenant-admin/payments").then((r) => { setP(r.data); setKid(r.data.key_id); }); }, []);
  if (!p) return null;
  const save = async (enabled) => {
    setBusy(true);
    try {
      const r = await client.put("/tenant-admin/payments", { key_id: kid, key_secret: sec || null, enabled });
      setP(r.data); setSec("");
      toast.success(enabled ? "Your Razorpay account is now collecting payments" : "Saved");
    } catch (x) { err(x, "Could not save"); } finally { setBusy(false); }
  };
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
      <Card>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#1B1F5B]">Collect payments in your own Razorpay</h3>
          <div className="flex items-center gap-2 text-sm">
            <span data-testid="payments-status">{p.enabled ? "Connected" : "Using platform gateway"}</span>
            <Switch checked={p.enabled} disabled={busy} onCheckedChange={(v) => save(v)} data-testid="payments-enabled-switch" />
          </div>
        </div>
        <div className="mt-5 space-y-3">
          <Input placeholder="Key ID (rzp_live_… or rzp_test_…)" value={kid} onChange={(e) => setKid(e.target.value)} data-testid="payments-key-id-input" />
          <Input type="password" placeholder={p.has_secret ? "•••••••• saved (enter to replace)" : "Key Secret"} value={sec}
            onChange={(e) => setSec(e.target.value)} data-testid="payments-key-secret-input" />
        </div>
        <Button onClick={() => save(p.enabled)} disabled={busy} variant="outline" className="mt-4" data-testid="payments-save-btn">Save keys</Button>
      </Card>
      <div className="space-y-3 rounded-2xl bg-[#FFF9EC] p-5 text-sm leading-relaxed text-slate-600">
        <ShieldCheck className="h-5 w-5 text-[#B57A00]" />
        <p>Keys are verified with Razorpay before switching on and the secret is stored encrypted — it is never shown again.</p>
        <p><b>When connected:</b> your customers pay you directly; the net fare is shown as owed to the platform in Earnings.</p>
        <p><b>When off:</b> the platform collects payment and your markup is shown as payable to you.</p>
      </div>
    </div>
  );
};

const Kpi = ({ label, value, id, tone = "text-[#1B1F5B]" }) => (
  <div className="rounded-xl border border-slate-100 bg-white p-4" data-testid={`earnings-${id}`}>
    <p className="text-xs uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`mt-1 text-xl font-bold ${tone}`}>{value}</p>
  </div>
);

export const EarningsPanel = () => {
  const [e, setE] = useState(null);
  useEffect(() => { client.get("/tenant-admin/earnings").then((r) => setE(r.data)); }, []);
  if (!e) return null;
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <Kpi id="sales" label="Total sales" value={fmt(e.sales)} />
        <Kpi id="markup" label="Markup earned" value={fmt(e.markup_earned)} tone="text-[#0E7C6B]" />
        <Kpi id="payable" label="Payable to you" value={fmt(e.payable_to_agency)} />
        <Kpi id="receivable" label="You owe platform" value={fmt(e.receivable_from_agency)} />
        <Kpi id="net" label="Net settlement" value={fmt(e.net_settlement)} tone={e.net_settlement >= 0 ? "text-[#0E7C6B]" : "text-red-600"} />
      </div>
      <Card className="overflow-x-auto p-0">
        <table className="w-full text-left text-sm" data-testid="earnings-table">
          <thead className="bg-[#F5F7FB] text-xs uppercase text-slate-500">
            <tr>{["Ref", "Type", "Route", "Customer paid", "Net", "Your markup", "Collected by"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr>
          </thead>
          <tbody>
            {e.rows.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400"><Wallet className="mx-auto mb-2 h-6 w-6" />No sales yet — earnings appear after the first booking on your site.</td></tr>}
            {e.rows.map((r) => (
              <tr key={r.booking_id} className="border-t border-slate-100">
                <td className="px-4 py-3">{r.pnr || r.booking_id}</td><td className="px-4 py-3">{r.type || "flight"}</td>
                <td className="px-4 py-3">{r.route}</td><td className="px-4 py-3">{fmt(r.total)}</td>
                <td className="px-4 py-3">{fmt(r.net_amount ?? r.total - (r.agency_markup || 0))}</td>
                <td className="px-4 py-3 text-[#0E7C6B]">{fmt(r.agency_markup)}</td>
                <td className="px-4 py-3">{r.gateway === "tenant" ? "You" : "Platform"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
};

const blankPkg = { title: "", destination: "", country: "India", category: "Domestic", nights: 3, days: 4, price: 0, oldPrice: 0, image: "", summary: "", inclusions: [], exclusions: [], itinerary: [], active: true };
const lines = (a) => (a || []).join("\n");
const unlines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

const PackageEditor = ({ value, onClose, onSaved }) => {
  const [p, setP] = useState({ ...blankPkg, ...value });
  const [itin, setItin] = useState((value.itinerary || []).map((d) => `${d.title || ""} | ${d.description || d.desc || ""}`).join("\n"));
  const set = (k, v) => setP({ ...p, [k]: v });
  const save = async () => {
    const itinerary = unlines(itin).map((l, i) => { const [t, ...d] = l.split("|"); return { day: i + 1, title: t.trim(), desc: d.join("|").trim() }; });
    const body = { ...p, nights: Number(p.nights), days: Number(p.days), price: Number(p.price), oldPrice: Number(p.oldPrice) || 0, itinerary };
    ["package_id", "slug", "tenant_id", "created_at", "updated_at", "rating", "_id"].forEach((k) => delete body[k]);
    try {
      if (value.package_id) await client.put(`/tenant-admin/packages/${value.package_id}`, body);
      else await client.post("/tenant-admin/packages", body);
      toast.success("Package saved"); onSaved();
    } catch (x) { err(x, "Please fill title, destination and price"); }
  };
  const f = (k, ph, id, type = "text") => <Input type={type} placeholder={ph} value={p[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={id} />;
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="package-editor">
        <DialogHeader><DialogTitle>{value.package_id ? "Edit package" : "New package"}</DialogTitle></DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">{f("title", "Package title", "pkg-title-input")}</div>
          {f("destination", "Destination", "pkg-destination-input")}
          <select value={p.category} onChange={(e) => set("category", e.target.value)} className="rounded-md border px-3 text-sm" data-testid="pkg-category-select">
            {["Domestic", "International", "Honeymoon", "Pilgrimage", "Adventure", "Weekend"].map((c) => <option key={c}>{c}</option>)}
          </select>
          {f("nights", "Nights", "pkg-nights-input", "number")}{f("days", "Days", "pkg-days-input", "number")}
          {f("price", "Net price ₹ (markup added on site)", "pkg-price-input", "number")}{f("oldPrice", "Strike-through price ₹", "pkg-oldprice-input", "number")}
          <div className="sm:col-span-2">{f("image", "Cover image URL", "pkg-image-input")}</div>
          <Textarea className="sm:col-span-2" placeholder="Overview" value={p.summary} onChange={(e) => set("summary", e.target.value)} data-testid="pkg-summary-input" />
          <Textarea className="sm:col-span-2 min-h-[110px]" placeholder={"Day-wise itinerary — one line per day:\nArrival in Goa | Pick-up and hotel check-in"} value={itin} onChange={(e) => setItin(e.target.value)} data-testid="pkg-itinerary-input" />
          <Textarea placeholder="Inclusions (one per line)" value={lines(p.inclusions)} onChange={(e) => set("inclusions", unlines(e.target.value))} data-testid="pkg-inclusions-input" />
          <Textarea placeholder="Exclusions (one per line)" value={lines(p.exclusions)} onChange={(e) => set("exclusions", unlines(e.target.value))} data-testid="pkg-exclusions-input" />
          <label className="flex items-center gap-2 text-sm"><Switch checked={p.active} onCheckedChange={(v) => set("active", v)} data-testid="pkg-active-switch" />Visible on site</label>
        </div>
        <Button onClick={save} className="mt-2 bg-[#1B1F5B] hover:bg-[#141844]" data-testid="pkg-save-btn">Save package</Button>
      </DialogContent>
    </Dialog>
  );
};

const PkgRow = ({ p, children, testId }) => (
  <div className="flex items-center gap-4 border-t border-slate-100 py-3" data-testid={testId}>
    {p.image ? <img src={p.image} alt="" className="h-12 w-16 rounded-md object-cover" /> : <div className="h-12 w-16 rounded-md bg-slate-100" />}
    <div className="min-w-0 flex-1">
      <p className="truncate font-semibold text-[#1B1F5B]">{p.title}</p>
      <p className="text-xs text-slate-500">{p.destination} · {p.nights}N/{p.days}D · net {fmt(p.price)}</p>
    </div>
    {children}
  </div>
);

export const PackagesPanel = () => {
  const [data, setData] = useState({ own: [], shared: [] });
  const [edit, setEdit] = useState(null);
  const load = () => client.get("/tenant-admin/packages").then((r) => setData(r.data));
  useEffect(() => { load(); }, []);
  const open = async (id) => setEdit(id ? (await client.get(`/tenant-admin/packages/${id}`)).data : {});
  const del = async (id) => { if (window.confirm("Delete this package?")) { await client.delete(`/tenant-admin/packages/${id}`); load(); } };
  const vis = async (p) => {
    await client.post(`/tenant-admin/packages/${p.package_id}/visibility`, { hidden: !p.hidden });
    toast.success(p.hidden ? "Now selling this package" : "Hidden from your site"); load();
  };
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <Card>
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-bold text-[#1B1F5B]">Your packages ({data.own.length})</h3>
          <Button size="sm" onClick={() => open(null)} className="bg-[#F5B32B] text-[#1B1F5B] hover:bg-[#E89A10]" data-testid="pkg-add-btn"><Plus className="mr-1 h-4 w-4" />New</Button>
        </div>
        <div className="mt-3">
          {data.own.length === 0 && <p className="py-8 text-center text-sm text-slate-400">Create packages only your site sells.</p>}
          {data.own.map((p) => (
            <PkgRow key={p.package_id} p={p} testId={`own-pkg-${p.package_id}`}>
              {!p.active && <span className="text-xs text-slate-400">Hidden</span>}
              <Button size="icon" variant="ghost" onClick={() => open(p.package_id)} data-testid={`own-pkg-edit-${p.package_id}`}><Pencil className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => del(p.package_id)} data-testid={`own-pkg-delete-${p.package_id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </PkgRow>
          ))}
        </div>
      </Card>
      <Card>
        <h3 className="text-lg font-bold text-[#1B1F5B]">Shared catalogue ({data.shared.filter((p) => !p.hidden).length}/{data.shared.length} selling)</h3>
        <div className="mt-3 max-h-[560px] overflow-y-auto pr-1">
          {data.shared.map((p) => (
            <PkgRow key={p.package_id} p={p} testId={`shared-pkg-${p.package_id}`}>
              <Button size="sm" variant={p.hidden ? "outline" : "ghost"} onClick={() => vis(p)} data-testid={`shared-pkg-toggle-${p.package_id}`}>
                {p.hidden ? <><EyeOff className="mr-1 h-4 w-4" />Hidden</> : <><Eye className="mr-1 h-4 w-4" />Selling</>}
              </Button>
            </PkgRow>
          ))}
        </div>
      </Card>
      {edit && <PackageEditor value={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />}
    </div>
  );
};

export const LogoThumb = ({ url }) => (url ? <img src={assetUrl(url)} alt="" className="h-10 max-w-[140px] object-contain" /> : null);
